/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CreateTemplateInstanceWizardSteps,
    DefaultDynamicRouteInput,
    DefaultFallbackPolicyInput,
    DefaultStaticRouteInput,
    Mode,
    templateInstanceAllMustRespondVar,
    templateInstanceApproversVar,
    templateInstanceCategoryVar,
    templateInstanceCustomResponseOptionsVar,
    templateInstanceDescriptionVar,
    templateInstanceIconBackgroundColorVar,
    templateInstanceIconNameVar,
    templateInstanceIdVar,
    templateInstanceIsCustomResponseEnabledVar,
    templateInstanceSpecificPeopleVar,
    templateInstanceStatusVar,
    templateInstanceTitleVar,
    updateTemplateInstanceInputCopyVar,
} from '../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    showTemplateManagementCoachButtonMark,
    TemplateScope,
} from '../apollo/reactiveVariables/TemplateManagementVariables';
import directManagerPreviewPersonaImg from '../assets/direct-manager-preview-persona.png';
import { Constants } from '../common/Constants';
import { useModalDataQuery } from '../data/client/queries/ModalData.generated';
import { IECSConfigState } from '../ecs/EcsFlags';
import { IHost } from '../hosts/IHost';
import { ApprovalsLocalization } from '../localization';
import { LogCategory, ScenarioName, StepName } from '../logger';
import { ApprovalPersonaProps } from '../models/ApprovalPersonaProps';
import { FileAttachment } from '../models/fileService/FileAttachment';
import { FormsIframeMessageType } from '../models/formsIframe/FormsIframe';
import { ApprovalTemplateInstanceBasicView, ExpandValue, TeamTagMember, TeamTagResult } from '../service/gen';
import {
    ApprovalTemplateCategory,
    ApprovalTemplateInstance,
    ApprovalTemplateInstanceScopeType,
    DynamicRoute,
    DynamicRouteInput,
    DynamicRouteItem,
    DynamicRouteItemInput,
    DynamicRouteType,
    FallbackPolicy,
    FallbackPolicyInput,
    FallbackPolicyType,
    GetTemplatePromotionClient,
    PeopleResult,
    PredefinedApproversRouteResolveResult,
    PromotionInfo,
    RouteResolveResultCode,
    RouteTypes,
    StaticRoute,
    StaticRouteInput,
    StepRouteSetting,
    StepRouteSettingInput,
    UpdateApprovalTemplateInstanceInput,
    VisibilityPrincipal,
    VisibilityPrincipalInput,
    WorkflowSchema,
    WorkflowSchemaInput,
} from '../service/gen/templates';
import type { PeopleItem } from '../views/common/StepApproversPicker/PeoplePickerList.component';
import { UI_StepRouteSettingInput } from '../views/components/CreateTemplateInstanceWizard/steps/WorkflowDesign/TemplateApproverSettings/TemplateSequentialApproverSettings';
import { createPostMessageSender, PostMessage, PostMessageSender, setupPostMessageReceiver } from './IFrameUtils';
import { sortByText } from './PeoplePickerUtils';
import { getAppHost } from './TeamsUtils';
import { batchGetTeamTag } from './TeamTagPeoplePickerUtils';
import { getFormsEndpoint, stringFormat } from './Utils';
export interface TemplateCategoryOption {
    key: ApprovalTemplateCategory;
    text: string;
}

export enum FormsServiceErrorCode {
    GroupNotFound = '625',
    NotMemberOfGroup = '707',
    NotOwnerOfGroup = 'Failed to create form',
    NotTeamOwner = '5901',
    LicenseNotFound = '751',
    LicenseExpired = '752',
    ReachFormsLimit = '650',
}

export interface Template {
    id: string;
    title: string;
    description?: string;
    formId?: string;
    category?: ApprovalTemplateCategory;
    iconName?: string;
    iconBackgroundColor?: string;
    formTemplate?: string;
    tag?: string;
}

export interface TemplateForCard {
    id: string;
    title: string;
    description?: string;
    iconName?: string;
    iconBackgroundColor?: string;
    showPromotion?: boolean;
}

export const validIsNew = (selfTags: string[], validNewTags: string[]): boolean => {
    for (const tag of selfTags) {
        if (validNewTags.includes(tag)) return true;
    }
    return false;
};

export const template2TemplateForCard = (
    template: Template,
    showCategory?: boolean,
    inPromotion?: boolean
): TemplateForCard => ({
    id: template.id,
    title: template.title,
    description:
        showCategory && getCategoryText(template.category) ? getCategoryText(template.category) : template.description,
    iconName: template.iconName,
    iconBackgroundColor: template.iconBackgroundColor,
    showPromotion: !!inPromotion,
});

export const defaultTemplateForCard: TemplateForCard = {
    id: '0000-0000-0000-0000',
    title: 'From Scratch',
    iconName: 'AppIconDefault',
    iconBackgroundColor: '#1D5190',
};

export enum FormsIframeErrorCode {
    LoadTimeout = 'LoadTimeout',
    FormIsDeleted = '674',
}

export class TemplateConstants {
    public static commonIframeTimeout = 20000;
}

export enum SubstrateServiceErrorCode {
    SDSTenantShardProvisioningInProgress = 'SDSTenantShardProvisioningInProgress',
    ErrorInvalidOrgPartition = 'ErrorInvalidOrgPartition',
}

let categoryOptions: TemplateCategoryOption[] = [];

let categoryMap: Map<ApprovalTemplateCategory, string> = new Map();

export function initiateTemplateCategories(loc: ApprovalsLocalization, _ecs: IECSConfigState): void {
    // no need to consider the loc change since will reload page.
    categoryOptions = [
        {
            key: ApprovalTemplateCategory.Activity,
            text: loc.strings.template_category_activity,
        },
        {
            key: ApprovalTemplateCategory.Administration,
            text: loc.strings.template_category_administration,
        },
        {
            key: ApprovalTemplateCategory.Finance,
            text: loc.strings.template_category_finance,
        },
        {
            key: ApprovalTemplateCategory.OrderManagement,
            text: loc.strings.template_category_order_management,
        },
        {
            key: ApprovalTemplateCategory.NotSpecified,
            text: loc.strings.template_category_others,
        },
        {
            key: ApprovalTemplateCategory.Attendance,
            text: loc.strings.template_category_attendance,
        },
        {
            key: ApprovalTemplateCategory.WordAddin,
            text: loc.strings.template_category_word,
        },
    ];

    categoryMap = new Map(
        categoryOptions.map((option: TemplateCategoryOption): [ApprovalTemplateCategory, string] => [
            option.key,
            option.text,
        ])
    );
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

export function getCategoryOptions(): TemplateCategoryOption[] {
    categoryOptions.sort((a: TemplateCategoryOption, b: TemplateCategoryOption): number => {
        return collator.compare(a.text, b.text);
    });
    if (!getAppHost().getEcsConfig().MicrosoftTeamApprovals.CreateWordCategoryEnabled) {
        categoryOptions = categoryOptions.filter(
            (categoryOption: TemplateCategoryOption): boolean =>
                categoryOption.key !== ApprovalTemplateCategory.WordAddin
        );
    }
    return categoryOptions;
}

export function getCategoryText(category: ApprovalTemplateCategory): string {
    return categoryMap.get(category);
}

export function getTemplateScenarioName(mode: Mode): ScenarioName {
    if (mode === Mode.Create) {
        return ScenarioName.CreateTemplateInstance;
    } else if (mode === Mode.Edit) {
        return ScenarioName.EditTemplateInstance;
    }
}

const stepButtonIdArray = ['BasicSettingsButton', 'FormDesignButton', 'WorkflowSettingsButton'];

export function getButtonIdByCurrentStep(currentStep: CreateTemplateInstanceWizardSteps): string {
    return stepButtonIdArray[currentStep - 1];
}

export function getIdSeletorByCurrentStep(currentStep: CreateTemplateInstanceWizardSteps): string {
    return '#' + stepButtonIdArray[currentStep - 1];
}

const stepArray = [
    StepName.EnterWizardBasicSettings,
    StepName.EnterWizardFormDesign,
    StepName.EnterWizardWorkflowSettings,
];

export function getStepNameByCurrentStep(currentStep: CreateTemplateInstanceWizardSteps): string {
    return stepArray[currentStep - 1];
}

/**
 * Compare two objects, append diff prop in newObj to final result
 *
 * @param originObj origin object
 * @param newObj new object
 * @returns Object include all props which newObj differs from originObj
 */
export function getPatchObj<T>(originObj: T, newObj: T): T {
    if (!newObj || !originObj) {
        return {} as T;
    }

    return Object.keys(newObj).reduce((diff: any, key: string): any => {
        if (originObj[key] === newObj[key]) {
            return diff;
        }

        if (Array.isArray(newObj[key])) {
            if (JSON.stringify(newObj[key]) === JSON.stringify(originObj[key])) {
                return diff;
            }
        } else if (typeof newObj[key] === 'object') {
            const diffObj = getPatchObj(originObj[key], newObj[key]);
            if (isEmpty(diffObj)) {
                return diff;
            } else {
                return {
                    ...diff,
                    [key]: diffObj,
                };
            }
        }

        return {
            ...diff,
            [key]: newObj[key],
        };
    }, {});
}

export enum CoachMark {
    TemplateManagementEntry = 'TemplateManagementEntryCoachMark',
    OuterTemplatePromotion = 'OuterTemplatePromotion',
    InnerTemplatePromotion = 'InnerTemplatePromotion',
    TemplateListScope = 'TemplateListScopeCoachMark',
    DataExport = 'DataExport',
    SequentialApprovalEntryButton = 'SequentialApprovalEntryButtonCoachMark',
    SequentialApprovalEntrySwitch = 'SequentialApprovalEntrySwitchCoachMark',
    SequentialApprovalEntrySwitchCoachMarkInTemplate = 'SequentialApprovalEntrySwitchCoachMarkInTemplate',
    ShareTemplateLinkEntry = 'ShareTemplateLinkEntryCoachMark',
}

const nextOpenShowCoachMark: { [key in CoachMark]?: CoachMark } = {
    [CoachMark.OuterTemplatePromotion]: CoachMark.TemplateManagementEntry,
    [CoachMark.SequentialApprovalEntryButton]: CoachMark.TemplateManagementEntry,
    [CoachMark.DataExport]: CoachMark.SequentialApprovalEntryButton,
};

type CoachMarkStatusMap = { [key in CoachMark]?: boolean };

const nextCoachMarkStatusMap = Object.keys(nextOpenShowCoachMark).reduce(
    (result: CoachMarkStatusMap, coachMark: string): CoachMarkStatusMap => {
        result[coachMark] = !!localStorage.getItem(nextOpenShowCoachMark[coachMark]);
        return result;
    },
    {} as CoachMarkStatusMap
);

const getAllowShow = (coachMark: CoachMark): boolean => {
    if (nextOpenShowCoachMark[coachMark]) {
        return nextCoachMarkStatusMap[coachMark];
    }
    return true;
};

export const useCoachMarkState = (coachMark: CoachMark, onModelDialog: boolean = false): [boolean, () => void] => {
    const [showCoachMark, setShowCoachMark] = useState<boolean>(!localStorage.getItem(coachMark));
    const { data } = useModalDataQuery();
    const isAllow = getAllowShow(coachMark);
    const isOpen = showCoachMark && (!data?.modalData.isOpen || onModelDialog) && isAllow;

    return [
        isOpen,
        (): void => {
            if (isOpen) {
                localStorage.setItem(coachMark, 'hide');
                setShowCoachMark(false);
            }
        },
    ];
};

export const useValidTemplatePromotion = (): { validPromotions: PromotionInfo[] } => {
    const [validPromotions, setValidPromotions] = useState<PromotionInfo[]>([]);
    // get latest template promotions
    useEffect((): void => {
        const nowTimeStamp = Date.now();
        new GetTemplatePromotionClient()
            .getTemplatePromotion()
            .then((res: PromotionInfo[]): void => {
                const validPromotionList = res.filter(
                    (item: PromotionInfo): boolean => new Date(item.ExpiredTime).getTime() > nowTimeStamp
                );
                setValidPromotions(validPromotionList);
            })
            .catch((e: Error): void => {
                getAppHost()
                    .getLogger()
                    .logTraceError(LogCategory.TemplatePromotion, `Failed to handle template promotion: ${e}`);
            });
    }, []);
    return { validPromotions };
};

export const useTemplatePromotionCoachMarkState = (
    coachMark: CoachMark.InnerTemplatePromotion | CoachMark.OuterTemplatePromotion
): [boolean, () => void] => {
    const [showCoachMark, setShowCoachMark] = useState<boolean>(false);
    const { data: modal } = useModalDataQuery();
    const { validPromotions } = useValidTemplatePromotion();
    const isAllow = getAllowShow(coachMark);

    const hideCoachMark = (): void => {
        localStorage.setItem(coachMark, JSON.stringify(validPromotions));
        setShowCoachMark(false);
    };

    const checkPromotionChanged = useCallback(
        (storageKey: CoachMark.InnerTemplatePromotion | CoachMark.OuterTemplatePromotion): boolean => {
            const nowTimeStamp = Date.now();
            let oldValidPromotions: PromotionInfo[] = [];
            try {
                oldValidPromotions = (JSON.parse(localStorage.getItem(storageKey) ?? '[]') as PromotionInfo[]).filter(
                    (item: PromotionInfo): boolean =>
                        item?.ExpiredTime && new Date(item.ExpiredTime).getTime() > nowTimeStamp
                );
            } catch (e) {
                getAppHost()
                    .getLogger()
                    .logTraceError(LogCategory.TemplatePromotion, `Failed to handle template promotion: ${e}`);
            }
            for (const p of validPromotions) {
                if (
                    !oldValidPromotions.filter(
                        (item: PromotionInfo): boolean => item?.Tag === p?.Tag && item?.ExpiredTime === p?.ExpiredTime
                    )?.length
                ) {
                    return true;
                }
            }
            return false;
        },
        [validPromotions]
    );

    // The logic to show the template promotion coach mark
    useEffect((): void => {
        if (checkPromotionChanged(coachMark)) {
            switch (coachMark) {
                case CoachMark.InnerTemplatePromotion:
                    setShowCoachMark(true);
                    break;
                case CoachMark.OuterTemplatePromotion:
                    if (
                        localStorage.getItem(CoachMark.TemplateManagementEntry) &&
                        checkPromotionChanged(CoachMark.InnerTemplatePromotion)
                    ) {
                        setShowCoachMark(true);
                    }
                    break;
            }
        }
    }, [checkPromotionChanged, coachMark]);
    return [showCoachMark && isAllow && modal?.modalData?.isOpen, hideCoachMark];
};

// Custom hook for use template management coach mark.
// 1. Check local stroage first to initialize if we display coach mark.
// 2. return [showCoachMark: boolean, hideCoachMark: () => void] for invoker to use.
export const useTemplateManagementButtonCoachMarkState = (): [boolean, () => void] => {
    const storageItemKey = CoachMark.TemplateManagementEntry;
    const storageItemValue = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TemplateManagementButtonCoachMarkValue;
    const showCoachMark = useReactiveVar(showTemplateManagementCoachButtonMark);

    // Because ReactiveVar lack the ability of inital function, so we do the initialization in useEffect hook.
    useEffect((): void => {
        if (!!storageItemValue && localStorage.getItem(storageItemKey) !== storageItemValue) {
            showTemplateManagementCoachButtonMark(true);
        }
    }, [storageItemKey, storageItemValue]);

    return [
        showCoachMark,
        (): void => {
            localStorage.setItem(storageItemKey, storageItemValue);
            showTemplateManagementCoachButtonMark(false);
        },
    ];
};

/**
 * Get text string of enum ApprovalTemplateInstanceScopeType
 *
 * @param type enum ApprovalTemplateInstanceScopeType
 * @returns text string of the enum value
 */
export function getApprovalTemplateInstanceScopeTypeText(type: ApprovalTemplateInstanceScopeType): string {
    switch (type) {
        case ApprovalTemplateInstanceScopeType.Organization: {
            return 'Organization';
        }
        case ApprovalTemplateInstanceScopeType.Team: {
            return 'Team';
        }
        default: {
            return 'Basic';
        }
    }
}

/**
 * Remove duplicate personas
 *
 * @param personas
 * @returns a array of personas
 */
export function distinctPersonas(personas: IPersonaProps[]): IPersonaProps[] {
    const personasObj = {};
    const result: IPersonaProps[] = [];
    personas.forEach((p: IPersonaProps): void => {
        if (!(p.id in personasObj)) {
            personasObj[p.id] = p;
        }
    });
    Object.keys(personasObj).forEach((k: string): void => {
        result.push(personasObj[k]);
    });
    return result;
}

async function getApproversV2(
    workflowSchema?: WorkflowSchema,
    preview?: boolean
): Promise<{
    approvers: IPersonaProps[];
    approversPickerDisabled?: boolean;
    allMustRespond?: boolean;
    tooltip?: string;
}> {
    const loc = getAppHost().getLocale();
    switch (workflowSchema?.RouteTypes) {
        case RouteTypes.LetUserEnter: {
            return { approvers: [], allMustRespond: true };
        }
        case RouteTypes.StaticRoute: {
            const staticApprovers: IPersonaProps[] = [];
            if (preview) {
                workflowSchema.StaticRoute?.ResolveResult.forEach(
                    (result: PredefinedApproversRouteResolveResult): void => {
                        staticApprovers.push(PeopleResult2IPersonaProps(result.Approver));
                    }
                );
            } else {
                workflowSchema.StaticRoute?.ResolveResult.forEach(
                    (result: PredefinedApproversRouteResolveResult): void => {
                        if (result.ResultCode === RouteResolveResultCode.Success) {
                            staticApprovers.push(PeopleResult2IPersonaProps(result.Approver));
                        } else {
                            // TODO(wahan) if we have enhanced error message for get approver user in AAD failures, we can add code here
                            throw new Error('Approver not found');
                        }
                    }
                );
            }

            return {
                approvers: staticApprovers,
                approversPickerDisabled: true,
                allMustRespond: workflowSchema.StaticRoute.AllMustRespond ?? true,
            };
        }
        case RouteTypes.DynamicRoute: {
            if (preview) {
                return {
                    approvers: [
                        {
                            text: loc.strings.direct_manager,
                            imageUrl: directManagerPreviewPersonaImg,
                        },
                    ],
                    approversPickerDisabled: true,
                    allMustRespond: workflowSchema.DynamicRoute.AllMustRespond ?? true,
                };
            }

            let dynamicApprovers: IPersonaProps[] = [];
            let approversPickerDisabled: boolean;
            let tooltip: string;
            let allMustRespond = true;
            const dynamicRoute = GetDynamicRouteItemsByType(
                workflowSchema.DynamicRoute,
                DynamicRouteType.DirectManager
            )[0];
            switch (dynamicRoute.ResultCode) {
                case RouteResolveResultCode.Success: {
                    dynamicApprovers = PeopleResults2IPersonaProps(dynamicRoute.Approvers);
                    approversPickerDisabled = true;
                    tooltip = loc.strings.approver_is_direct_manager;
                    break;
                }
                // NOTE We only support fallback policy when use direct manager feature currently.
                case RouteResolveResultCode.DirectManagerNotFound: {
                    const fallbackPolicy = workflowSchema.FallbackPolicy;
                    switch (fallbackPolicy?.Type) {
                        case FallbackPolicyType.LetUserEnter: {
                            approversPickerDisabled = false;
                            tooltip = loc.strings.direct_manager_fallback_tooltip_let_user_eneter;
                            break;
                        }
                        case FallbackPolicyType.FallbackApprovers: {
                            // NOTE Backend will resolve predefined approvers in fallback policy into DynamicRoute field.
                            dynamicApprovers = PredefinedApproversRouteResolveResults2IPersonaProps(
                                fallbackPolicy.FallbackApprovers
                            );
                            approversPickerDisabled = true;
                            tooltip = loc.strings.direct_manager_fallback_tooltip_preset_backup;
                            allMustRespond = fallbackPolicy.FallbackApproversAllMustRespond ?? true;
                            break;
                        }
                        default:
                            throw new Error('Invalid fallback policy');
                    }
                    break;
                }
                default:
                    throw new Error('Invalid dynamic route type');
            }
            return {
                approvers: dynamicApprovers,
                approversPickerDisabled,
                tooltip,
                allMustRespond,
            };
        }
        case MixedRouteType: {
            let staticApprovers: IPersonaProps[] = [];
            const teamTagDynamicRouteItems = GetDynamicRouteItemsByType(
                workflowSchema?.DynamicRoute,
                DynamicRouteType.TeamWorkTag
            );
            if (preview) {
                workflowSchema.StaticRoute?.ResolveResult.forEach(
                    (result: PredefinedApproversRouteResolveResult): void => {
                        staticApprovers.push(PeopleResult2IPersonaProps(result.Approver));
                    }
                );
                if (teamTagDynamicRouteItems?.length) {
                    const teamId = teamTagDynamicRouteItems[0].TeamId;
                    const teamTadIds = teamTagDynamicRouteItems.map((item: DynamicRouteItem): string => item.TeamTagId);
                    try {
                        const teamTagInfos = await batchGetTeamTag(teamId, teamTadIds, ExpandValue.Members);
                        teamTagInfos?.forEach((tag: TeamTagResult): void => {
                            staticApprovers = staticApprovers.concat(
                                tag.Members.map((m: TeamTagMember): IPersonaProps => TeamTagMember2IPersonaProps(m, 24))
                            );
                        });
                    } catch (e) {
                        throw new Error('Failed to expand Team Tags');
                    }
                }
            } else {
                workflowSchema.StaticRoute?.ResolveResult?.forEach(
                    (result: PredefinedApproversRouteResolveResult): void => {
                        if (result.ResultCode === RouteResolveResultCode.Success) {
                            staticApprovers.push(PeopleResult2IPersonaProps(result.Approver));
                        } else {
                            // TODO(wahan) if we have enhanced error message for get approver user in AAD failures, we can add code here
                            throw new Error('Approver not found');
                        }
                    }
                );
                teamTagDynamicRouteItems.forEach((ditem: DynamicRouteItem): void => {
                    if (ditem?.ResultCode === RouteResolveResultCode.Success) {
                        staticApprovers = staticApprovers.concat(ditem.Approvers.map(PeopleResult2IPersonaProps));
                    } else {
                        throw new Error(`Team Tag resolve fail: ${ditem.ResultCode}`);
                    }
                });
            }
            const res = {
                approvers: distinctPersonas(staticApprovers),
                approversPickerDisabled: true,
                allMustRespond: workflowSchema?.StaticRoute.AllMustRespond ?? true,
            };
            return res;
        }
    }
}
/**
 * Get approvers from WorkflowSchema of ApprovalTemplateInstance
 *
 * @param workflowSchema workflow defined by the ApprovalTemplateInstance, containing approvers
 * @returns a array contain approvers info
 */
export async function getPresetsFromWorkflowSchema(
    workflowSchema?: WorkflowSchema,
    preview?: boolean
): Promise<
    | {
          approvers: IPersonaProps[];
          approversPickerDisabled?: boolean;
          allMustRespond?: boolean;
          tooltip?: string;
          isSequential?: boolean;
      }
    | { stepApprovers: PeopleItem[]; isSequential: true }
> {
    const workflowSchemaVersion = workflowSchema.Version ?? 'v1.0';
    switch (workflowSchemaVersion) {
        case 'v1.0': {
            const presetApprovers: IPersonaProps[] = [];
            workflowSchema?.Approvers?.forEach((approver: PeopleResult): void => {
                presetApprovers.push(PeopleResult2IPersonaProps(approver));
            });
            // In v1.0 workflow schema, if preset approvers is empty, allMustRespond force true, ignore backend return value.
            let allMustRespond = true;
            if (presetApprovers.length !== 0) {
                allMustRespond = workflowSchema?.AllMustRespond ?? true;
            }
            return {
                approvers: presetApprovers,
                allMustRespond,
                approversPickerDisabled: presetApprovers.length > 0,
            };
        }
        case 'v2.0': {
            if (workflowSchema.RouteTypes === RouteTypes.StepRoute) {
                const items = await getStepApproversItemsFromWorkflowSchema(workflowSchema, preview);
                return { stepApprovers: items, isSequential: true };
            }
            return getApproversV2(workflowSchema, preview);
        }
        default:
            throw new Error('Invalid workflow schema version');
    }
}

async function getStepApproversItemsFromWorkflowSchema(
    workflowSchema?: WorkflowSchema,
    preview?: boolean
): Promise<PeopleItem[]> {
    const items: PeopleItem[] = [];
    if (workflowSchema?.StepRoutes) {
        for (const step of workflowSchema.StepRoutes) {
            const approvers = await getApproversV2(step as WorkflowSchema, preview);
            items.push({
                order: items.length + 1,
                personaList: approvers.approvers,
                disabled: approvers.approversPickerDisabled,
                error: approvers.tooltip,
            });
        }
    }
    return items;
}
/**
 * Get url of form runtime iframe url
 *
 * @param formId the id of the embeded form defined by the ApprovalTemplateInstance
 * @param upn user principle name of current user, required by Form
 * @param theme name of current theme, reuqired by Form, use as Form initial theme
 * @param preview optional, indicate the if the embeded form should be readonly
 * @returns an url used to embed Form runtime iframe
 */
export function getFormsTemplatePreviewIframeUrl(): string {
    const host = getAppHost();
    const formsIframeUrl = new URL(Constants.FORMS_RUNTIME_IFRAME_PATH, getFormsEndpoint());
    formsIframeUrl.searchParams.append('host', 'TeamsApproval');
    formsIframeUrl.searchParams.append('auth_pvr', 'AAD');
    formsIframeUrl.searchParams.append('auth_upn', host.getUserPrincipalName());
    formsIframeUrl.searchParams.append('theme', host.getTheme());
    formsIframeUrl.searchParams.append('view', 'templatePreview');
    formsIframeUrl.searchParams.append('lang', host.getLocaleName());
    formsIframeUrl.searchParams.append('tid', host.getTenantId());

    return formsIframeUrl.toString();
}

/**
 * Get url of form runtime iframe url
 *
 * @param formId the id of the embeded form defined by the ApprovalTemplateInstance
 * @param upn user principle name of current user, required by Form
 * @param theme name of current theme, reuqired by Form, use as Form initial theme
 * @param preview indicating if we use this embeded page for preview. This parameter is for forms log purpose
 * @returns an url used to embed Form runtime iframe
 */
export function getFormsRuntimeIframeUrl(formId: string, preview?: boolean): string {
    const host = getAppHost();
    const formsIframeUrl = new URL(Constants.FORMS_RUNTIME_IFRAME_PATH, getFormsEndpoint());
    formsIframeUrl.searchParams.append('Id', formId);
    formsIframeUrl.searchParams.append('Host', 'TeamsApproval');
    formsIframeUrl.searchParams.append('auth_pvr', 'AAD');
    formsIframeUrl.searchParams.append('auth_upn', host.getUserPrincipalName());
    formsIframeUrl.searchParams.append('theme', host.getTheme());
    formsIframeUrl.searchParams.append('lang', host.getLocaleName());
    formsIframeUrl.searchParams.append('HostClientType', host.getHostClientType());
    formsIframeUrl.searchParams.append('tid', host.getTenantId());
    // NOTE this parameter is for Forms logging purpose.
    if (preview) formsIframeUrl.searchParams.append('view', 'preview');

    return formsIframeUrl.toString();
}

/* Interface defines forms runtime iframe event handlers */
export interface SizeChangedEvent {
    height: number;
    expanded?: boolean;
}

export interface KeyDownEvent {
    keyCode: number;
}

export interface ErrorEvent {
    code?: string;
    message?: string;
    isRetriable?: boolean;
}
export interface FormsRuntimeIframeEventHandlers {
    keyDownEventHandler?: (event: KeyDownEvent) => void;
    formReadyEventHandler?: () => void;
    sizeChangedEventHandler?: (event: SizeChangedEvent) => void;
    dataValidateEventHandler?: (result: boolean) => void;
    getTemplateEventHandler?: () => string;
    generalErrorHandler?: (event: ErrorEvent) => void;
    serverErrorHandler?: (event: ErrorEvent) => void;
    authErrorHandler?: (event: ErrorEvent) => void;
}

export const errorConvert = (message: PostMessage<FormsIframeMessageType>): ErrorEvent => ({
    code: message.payload.error.code,
    message: message.payload.error.message,
    isRetriable: message.payload.isRecoverable,
});

/**
 * Custom hook for use forms runtime iframe
 * This hook will maintain the communication with forms iframe
 * 1. Create/Update the message sender when the forms iframe changed
 * 2. Add/Remove listener for forms message when the forms iframe changed
 * And this hook will handle received forms events
 * 1. FormReady event: trigger callback when recv
 * 2. SizeChanged event: trigger callback everytime recv
 * 3. DataValidationResult event: trigger callback everytime recv
 * 4. GetTokenRequest event: return token(SSO token from teams for forms endpoint) automatically
 * And this hook will handle notification events to forms
 * 1. when theme change, a ThemeChanged event will be sent to forms iframe
 *
 * @param eventHandlers callback functions when receive forms message
 * @returns [
 * a React.RefObject that used to ref the the iframe element that embed form iframe,
 * a message sender that can post message to form runtime iframe
 * ]
 */
export const useFormsRuntimeIframe = (
    eventHandlers?: FormsRuntimeIframeEventHandlers,
    scrollRef?: React.MutableRefObject<HTMLDivElement>
): [
    React.MutableRefObject<HTMLIFrameElement>,
    PostMessageSender,
    React.MutableRefObject<HTMLDivElement>,
    SizeChangedEvent
] => {
    function usePrevious(value: SizeChangedEvent): SizeChangedEvent {
        const ref = useRef<SizeChangedEvent>();
        useEffect((): void => {
            ref.current = value;
        });
        return ref.current;
    }

    const formsEndpoint = getFormsEndpoint();
    const currentTheme = getAppHost().getTheme();

    const formsIframeRef = useRef<HTMLIFrameElement>(null);
    const _scrollRef = scrollRef ?? useRef<HTMLDivElement>(null);
    const [postMessageSender, setPostMessageSender] = useState<PostMessageSender>(null);
    const [iframeHeight, setIframeHeight] = useState<SizeChangedEvent>({ height: 400, expanded: false });
    const prevHeight = usePrevious(iframeHeight);

    const formsIframeMessageHandler = {
        // one-way messages
        [FormsIframeMessageType.KeyDown]: (event: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.keyDownEventHandler) eventHandlers.keyDownEventHandler(event.payload);
        },
        [FormsIframeMessageType.FormReady]: (_message: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.formReadyEventHandler) eventHandlers.formReadyEventHandler();
        },
        [FormsIframeMessageType.SizeChanged]: (message: PostMessage<FormsIframeMessageType>): void => {
            setIframeHeight(message.payload);
            if (eventHandlers?.sizeChangedEventHandler) eventHandlers.sizeChangedEventHandler(message.payload);
        },
        [FormsIframeMessageType.DataValidationResult]: (message: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.dataValidateEventHandler) {
                if (message.payload?.status === 'passed') eventHandlers.dataValidateEventHandler(true);
                else eventHandlers.dataValidateEventHandler(false);
            }
        },
        [FormsIframeMessageType.GeneralError]: (message: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.generalErrorHandler) eventHandlers.generalErrorHandler(errorConvert(message));
        },
        [FormsIframeMessageType.ServerError]: (message: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.serverErrorHandler) eventHandlers.serverErrorHandler(errorConvert(message));
        },
        [FormsIframeMessageType.AuthError]: (message: PostMessage<FormsIframeMessageType>): void => {
            if (eventHandlers?.authErrorHandler) eventHandlers.authErrorHandler(errorConvert(message));
        },
        // messages require response
        [FormsIframeMessageType.GetTokenRequest]: (message: PostMessage<FormsIframeMessageType>): void => {
            getAppHost()
                .getAuthToken(formsEndpoint)
                .then((token: string): void => {
                    postMessageSender.sendReply(message, { payload: { token } });
                });
        },
        [FormsIframeMessageType.GetTemplate]: (): void => {
            if (eventHandlers?.getTemplateEventHandler) {
                const template = eventHandlers.getTemplateEventHandler();
                postMessageSender.send({
                    type: FormsIframeMessageType.TemplateContent,
                    payload: {
                        content: template,
                    },
                });
            }
        },
    };

    // update postMessageSender/Receiver when iframe change
    useEffect((): (() => void) => {
        if (formsIframeRef.current) {
            setPostMessageSender(createPostMessageSender(window, formsIframeRef.current.contentWindow, formsEndpoint));
            const { remove } = setupPostMessageReceiver(window, formsEndpoint, formsIframeMessageHandler);

            return remove;
        }
    }, [formsIframeRef.current]);

    // scroll down the delta when height expanded
    useEffect((): void => {
        if (iframeHeight.expanded && iframeHeight.height > prevHeight.height) {
            _scrollRef.current?.scrollBy(0, iframeHeight.height - prevHeight.height);
        }
    }, [iframeHeight.height]);

    // notify forms iframe when theme changed
    useEffect((): void => {
        postMessageSender?.send({
            type: FormsIframeMessageType.ThemeChanged,
            payload: { theme: currentTheme },
        });
    }, [currentTheme]);

    return [formsIframeRef, postMessageSender, _scrollRef, iframeHeight];
};

/**
 * Custom hook for using the existing Approval Text Input component for title when create template approval
 *
 * @returns [title, setTitle], [titleError, setTitleError]: state/dispatcher of the text input component
 * onTitleChange: input component update callback
 */
export const useApprovalTitleInputField = (
    defaultTitle: string = null
): {
    title: string;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
    titleError: string;
    setTitleError: React.Dispatch<React.SetStateAction<string>>;
    onTitleChange: (errorMessage: string, newValue?: string) => void;
    validateTitle: (event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
} => {
    const [title, setTitle] = useState<string>(defaultTitle || '');
    const [titleError, setTitleError] = useState<string>(null);
    const titleRef = useRef<string>();
    titleRef.current = title;
    const onTitleChange = (_errorMessage: string, newValue: string): void => {
        setTitle(newValue);
        setTitleError(validateTitle(newValue));
    };
    const validate = (_event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setTitleError(validateTitle(titleRef.current));
    };

    return { title, setTitle, titleError, setTitleError, onTitleChange, validateTitle: validate };
};

export function useDuplicateManagerState(WorkflowSchema: WorkflowSchema): boolean {
    return useMemo((): boolean => {
        if (WorkflowSchema?.RouteTypes === RouteTypes.StepRoute) {
            const directManager = WorkflowSchema.StepRoutes.find(
                (step: StepRouteSetting): boolean =>
                    step.RouteTypes & RouteTypes.DynamicRoute &&
                    step.DynamicRoute?.Items?.[0]?.Type === DynamicRouteType.DirectManager &&
                    step.DynamicRoute?.Items?.[0]?.ResultCode === RouteResolveResultCode.Success
            );
            if (directManager) {
                const approverIds: string[] = [];
                for (const step of WorkflowSchema.StepRoutes) {
                    if (step.RouteTypes & RouteTypes.StaticRoute && step.StaticRoute?.ResolveResult) {
                        step.StaticRoute.ResolveResult.forEach((item: PredefinedApproversRouteResolveResult): void => {
                            approverIds.push(item.ApproverId);
                        });
                    }
                }
                const directManagerId = directManager.DynamicRoute?.Items?.[0]?.Approvers?.[0]?.UserAadObjectId;
                return approverIds.indexOf(directManagerId) !== -1;
            }
        }
        return false;
    }, [WorkflowSchema?.RouteTypes, WorkflowSchema?.StepRoutes]);
}
/**
 * Custom hook for using the existing Approval ApproverPick component in create template approval
 *
 * @returns [approvers, setApprovers], [approversError, setApproversError]: state/dispatcher of the approver picker
 * onApproversChange: input component update callback
 */
export const useApprovalApproversInputField = (
    workflowSchema?: WorkflowSchema,
    preview?: boolean
): {
    approvers: IPersonaProps[];
    approversError: string;
    workflowSchemaError: string;
    onApproversChange: (items: IPersonaProps[]) => void;
    onStepApproversChange: (updateCallback: (oldValue: PeopleItem[]) => PeopleItem[]) => void;
    validateApprovers(): void;
    approversPickerDisabled?: boolean;
    tooltip?: string;
    allMustRespond: boolean;
    setAllMustRespond: (newVal: boolean) => void;
    processing: boolean;
    isSequential?: boolean;
    stepApprovers?: PeopleItem[];
} => {
    // eslint-disable-next-line @typescript-eslint/tslint/config, react-hooks/exhaustive-deps
    const [templatePresets, setTemplatePresets] = useState<{
        approvers: IPersonaProps[];
        approversPickerDisabled?: boolean;
        allMustRespond?: boolean;
        tooltip?: string;
        stepApprovers?: PeopleItem[];
        isSequential: boolean;
    }>({
        approvers: [],
        isSequential: workflowSchema.RouteTypes === RouteTypes.StepRoute,
    });
    const [processing, setProcessing] = useState<boolean>(true);
    const [approvers, setApprovers] = useState<IPersonaProps[]>([]);
    const [stepApprovers, setStepApprovers] = useState<PeopleItem[]>([]);

    const [approversError, setApproversError] = useState<string>(null);
    const [workflowSchemaError, setworkflowSchemaError] = useState<string>();
    const [allMustRespond, setAllMustRespond] = useState<boolean>(true);
    const approversRef = useRef<IPersonaProps[]>();
    approversRef.current = approvers;
    const onApproversChange = (items: IPersonaProps[]): void => {
        setApprovers(items);
        setApproversError(validateApprovers(items));
    };
    const onStepApproversChange = (updateCallback: (oldValue: PeopleItem[]) => PeopleItem[]): void => {
        setStepApprovers((old: PeopleItem[]): PeopleItem[] => updateStepApprovers(updateCallback(old)));
    };
    const validate = (): void => {
        setApproversError(
            templatePresets.isSequential
                ? validateStepApprovers(stepApprovers)
                : validateApprovers(approversRef.current)
        );
    };

    useEffect((): (() => void | undefined) => {
        let isUnmount = false;
        getPresetsFromWorkflowSchema(workflowSchema, preview)
            .then(
                (tpresets: {
                    approvers: IPersonaProps[];
                    approversPickerDisabled?: boolean;
                    allMustRespond?: boolean;
                    tooltip?: string;
                    stepApprovers?: PeopleItem[];
                    isSequential: boolean;
                }): void => {
                    if (!isUnmount) {
                        setTemplatePresets(tpresets);
                        if (tpresets.isSequential) {
                            setStepApprovers(tpresets.stepApprovers);
                        } else {
                            setApprovers(tpresets.approvers);
                            setAllMustRespond(tpresets.allMustRespond);
                        }
                        setProcessing(false);
                    }
                }
            )
            .catch((e: any): void => {
                console.error(e);
                setworkflowSchemaError(e);
            });
        return (): void => {
            isUnmount = true;
        };
    }, [workflowSchema, preview]);

    return {
        ...templatePresets,
        approvers,
        stepApprovers,
        approversError,
        workflowSchemaError,
        onApproversChange,
        onStepApproversChange,
        validateApprovers: validate,
        allMustRespond,
        setAllMustRespond,
        processing,
    };
};

/**
 * Custom hook for using the existing Approval FileAttachment component in create template approval
 *
 * @returns [fileAttachments, setFileAttachments]: state/dispatcher of the file attachment component
 */
export const useFilesInputField = (): {
    fileAttachments: FileAttachment[];
    setFileAttachments: React.Dispatch<React.SetStateAction<FileAttachment[]>>;
} => {
    const [fileAttachments, setFileAttachments] = useState<FileAttachment[]>([]);
    return { fileAttachments, setFileAttachments };
};

/**
 * validator function of creating template approval component
 *
 * @param host IHost for get some global settings
 * @param workflowSchema workflow defined by the ApprovalTemplateInstance if any
 * @param title user input template approval request title
 * @param approvers user input template approval request approvers
 * @param fileAttachments user uploaded file attachements
 * @returns boolean indicating if is valid
 */
export function validateCreateTemplateApprovalInputFields({
    title,
    workflowSchema,
    host,
    approvers,
    showFileAttachment,
    fileAttachments,
    stepApprovers,
}: {
    host: IHost;
    workflowSchema: WorkflowSchema | undefined;
    title: string;
    approvers: IPersonaProps[];
    showFileAttachment: boolean;
    fileAttachments?: FileAttachment[];
    stepApprovers?: PeopleItem[];
}): boolean {
    // validate title field
    const isTitleValid = title.trim().length > 0;

    const maxNumberOfApprovers = host.getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
    // validate approver field
    let isApproversValid = false;
    if (workflowSchema?.RouteTypes === RouteTypes.StepRoute) {
        isApproversValid = !validateStepApprovers(stepApprovers);
    } else {
        isApproversValid = approvers?.length > 0 && approvers?.length <= maxNumberOfApprovers;
    }
    // validate file attachment field
    // only do the validation if it's visible, all files upload process should be done and at least have one file
    let isFileAttachmentValid = true;

    if (showFileAttachment) {
        isFileAttachmentValid =
            fileAttachments &&
            fileAttachments.length > 0 &&
            fileAttachments.filter(
                (file: FileAttachment): boolean => file.status !== UploadStatus.COMPLETE || !file.shareableUrl
            ).length === 0;
    }

    // validate custom response field
    const isCustomResponseEnabled = workflowSchema?.IsCustomResponseEnabled;
    const customResponseOptions = workflowSchema?.CustomResponseOptions;
    const requiredCustomResponseOptions = Constants.REQUIRED_CUSTOM_RESPONSE_OPTIONS;
    let isCustomResponsesValid = true;
    if (isCustomResponseEnabled) {
        if (customResponseOptions?.length < requiredCustomResponseOptions) {
            isCustomResponsesValid = false;
        } else {
            for (let i = 0; i < requiredCustomResponseOptions; i++) {
                if (customResponseOptions[i]?.trim().length === 0) {
                    isCustomResponsesValid = false;
                    break;
                }
            }
        }
    }

    return isTitleValid && isApproversValid && isFileAttachmentValid && isCustomResponsesValid;
}

/**
 * Custom hook for retreive team name by team id
 *
 * @param templateInstance: the template to get team Id from
 * @returns teamName: the team name if get team name successfully; return null when fetching or failure;
 */
export const useTeamName = (templateInstance: ApprovalTemplateInstanceBasicView): string => {
    const [teamName, setTeamName] = useState<string>(null);

    useEffect((): void => {
        const host = getAppHost();
        const loc = host.getLocale();
        if (templateInstance?.Scope === ApprovalTemplateInstanceScopeType.Organization) {
            setTeamName(loc.strings.org_wide_option_text);
        } else if (templateInstance?.Scope === ApprovalTemplateInstanceScopeType.Team) {
            host.getTeamName(templateInstance?.TeamId).then((name: string): void => {
                setTeamName(name);
            });
        } else {
            setTeamName(null);
        }
    }, [templateInstance]);

    return teamName;
};

/**
 * Custom hook handle iframe on focus event in reactjs
 *
 * @param iframeRef: the ref to the iframe to listen focus event
 * @param callBack: the callback to invoke when the onFocus event occurs
 */
export const useIframeOnFocus = (iframeRef: React.MutableRefObject<HTMLIFrameElement>, callBack: () => void): void => {
    useEffect((): (() => void) => {
        const onBlur = (): void => {
            if (document.activeElement === iframeRef.current) {
                callBack();
            }
        };

        window.addEventListener('blur', onBlur);

        return (): void => {
            window.removeEventListener('blur', onBlur);
        };
    }, []);
};

/* utility functions to convert between Template and ApprovalTemplateInstacne */
export const templates2TemplateInstances = (templates?: Template[]): Partial<ApprovalTemplateInstance>[] => {
    if (!templates) return [];
    else return templates.map(template2TemplateInstance);
};

export const templateInstances2templates = (instances: ApprovalTemplateInstance[]): Template[] => {
    if (!instances) return [];
    else return instances.map(templateInstance2Template);
};

export const template2TemplateInstance = (template: Template): Partial<ApprovalTemplateInstance> => ({
    AccentColor: template.iconBackgroundColor,
    IconName: template.iconName,
    Id: template.id,
    Title: template.title,
    Category: template.category,
    Description: template.description,
    FormId: template.formId,
});

export const templateInstance2Template = (instance: ApprovalTemplateInstance): Template => ({
    iconBackgroundColor: instance.AccentColor,
    iconName: instance.IconName,
    id: instance.Id,
    title: instance.Title,
    category: instance.Category,
    description: instance.Description,
    formId: instance.FormId,
});

export const useIsTemplateInstanceChanged = (currentMode: Mode): boolean => {
    const id = useReactiveVar(templateInstanceIdVar);
    const title = useReactiveVar(templateInstanceTitleVar);
    const description = useReactiveVar(templateInstanceDescriptionVar);
    const iconName = useReactiveVar(templateInstanceIconNameVar);
    const iconBackgroundColor = useReactiveVar(templateInstanceIconBackgroundColorVar);
    const specificPeople = useReactiveVar(templateInstanceSpecificPeopleVar);
    const category = useReactiveVar(templateInstanceCategoryVar);
    const approvers = useReactiveVar(templateInstanceApproversVar);
    const allMustRespond = useReactiveVar(templateInstanceAllMustRespondVar);
    const status = useReactiveVar(templateInstanceStatusVar);
    const isCustomResponseEnabled = useReactiveVar(templateInstanceIsCustomResponseEnabledVar);
    const customResponseOptions = useReactiveVar(templateInstanceCustomResponseOptionsVar);
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const updateTemplateInstanceInputCopy = useReactiveVar(updateTemplateInstanceInputCopyVar);

    // Due to React force align the count of hooks in each refresh, will move the check here
    if (currentMode === Mode.Create) {
        return true;
    }

    if (currentMode === Mode.None) {
        return false;
    }

    const updateInstanceInput: UpdateApprovalTemplateInstanceInput = {
        Category: category,
        Description: description,
        AccentColor: iconBackgroundColor,
        IconName: iconName,
        Title: title,
        WorkflowSchema: {
            Approvers: approvers?.map((approver: PeopleResult): string => {
                return approver.UserAadObjectId;
            }),
            AllMustRespond: allMustRespond,
            IsCustomResponseEnabled: isCustomResponseEnabled,
            CustomResponseOptions: isCustomResponseEnabled ? customResponseOptions : [],
        },
        Id: id,
        IsEnabled: status,
        VisibleTo: specificPeople?.map(
            (principal: VisibilityPrincipal): VisibilityPrincipalInput => ({
                PrincipalId: principal.PrincipalId,
                PrincipalType: principal.PrincipalType,
            })
        ),
        IsVisibleToAll: currentScope === TemplateScope.TenantAll,
    };

    const diffObj = getPatchObj(updateTemplateInstanceInputCopy, updateInstanceInput);
    return !isEmpty(diffObj);
};

export const validateTitle = (title?: string): string => {
    const loc = getAppHost().getLocale();

    if (!title) {
        return loc.strings.required;
    } else if (title.length === Constants.maxTitleCharacterLimit) {
        return stringFormat(loc.strings.maximum_character_error_text, {
            number_placeholder: Constants.maxTitleCharacterLimit.toString(),
        });
    } else {
        return null;
    }
};

export const validateApprovers = (approvers?: IPersonaProps[]): string => {
    const loc = getAppHost().getLocale();
    const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;

    if (!approvers || approvers.length === 0) {
        return loc.strings.required;
    } else if (approvers.length > maxNumberOfApprovers) {
        return loc.strings.maximum_approvers_error;
    } else {
        return null;
    }
};

export const validateStepApprovers = (stepApprovers?: PeopleItem[]): string => {
    if (
        !stepApprovers?.length ||
        stepApprovers.find((stepApprover: PeopleItem): boolean => !stepApprover.personaList?.length)
    ) {
        return getAppHost().getLocale().strings.required;
    }
    return stepApprovers?.find((stepApprover: PeopleItem): boolean => !!stepApprover.error && !stepApprover.disabled)
        ?.error;
};

export const updateStepApprovers = (stepApprovers?: PeopleItem[]): PeopleItem[] => {
    const loc = getAppHost().getLocale();
    const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
    const userIdList: string[] = [];
    for (const peopleItem of stepApprovers) {
        let hasError = false;
        if (!peopleItem?.personaList?.length) {
            peopleItem.error = loc.strings.required;
            hasError = true;
        } else if (peopleItem.personaList.length > maxNumberOfApprovers) {
            peopleItem.error = loc.strings.maximum_approvers_error;
            hasError = true;
        } else {
            for (const persona of peopleItem.personaList) {
                if (!userIdList.includes(persona.id)) {
                    userIdList.push(persona.id);
                } else if (!peopleItem.disabled) {
                    hasError = true;
                    peopleItem.error = loc.strings.duplicate_step_approvers_error;
                } else {
                    // If the step is disabled, we don't need to show the error
                    // look back to the previous step to see if it has the same approver
                    for (const previousStep of stepApprovers) {
                        if (previousStep === peopleItem) {
                            break; // loop to current step is enough
                        }
                        if (
                            !previousStep.disabled &&
                            previousStep.personaList?.find(
                                (personaItem: ApprovalPersonaProps): boolean => personaItem.id === persona.id
                            )
                        ) {
                            previousStep.error = loc.strings.duplicate_step_approvers_error;
                        }
                    }
                }
            }
        }
        if (!hasError) {
            peopleItem.error = undefined;
        }
    }
    return [...stepApprovers];
};

/* Type converters for PeopleResult, IPersonaProps, string */
export const PeopleResults2UserIds = (peopleResults: PeopleResult[]): string[] => {
    if (peopleResults?.length) {
        return peopleResults
            .filter((peopleResult: PeopleResult): boolean => !!peopleResult)
            .map((peopleResult: PeopleResult): string => peopleResult?.UserAadObjectId);
    } else {
        return [];
    }
};

export function PeopleResult2IPersonaProps(peopleResult: PeopleResult): ApprovalPersonaProps {
    return { id: peopleResult?.UserAadObjectId, text: peopleResult?.UserDisplayName };
}

export function GetDynamicRouteItemsByType(dynamicRoute: DynamicRoute, type: DynamicRouteType): DynamicRouteItem[] {
    return dynamicRoute?.Items?.filter((item: DynamicRouteItem): boolean => item.Type === type) ?? [];
}

export function GetDynamicRouteInputItemsByType(
    dynamicRoute: UI_DynamicRouteInput,
    type: DynamicRouteType
): DynamicRouteItemInput[] {
    return dynamicRoute?.Items.filter((item: UI_DynamicRouteItemInput): boolean => item.Type === type) ?? [];
}

export function TagDynamicItem2IPersonaProps(teamTagDynamicItem: UI_DynamicRouteItemInput): ApprovalPersonaProps {
    return {
        id: teamTagDynamicItem.TeamTagId,
        text: teamTagDynamicItem.TeamTagDisplayName,
        isTeamTag: true,
        teamId: teamTagDynamicItem.TeamId,
        teamTagMemberCount: teamTagDynamicItem.TeamTagMemberCount,
    };
}

export const TeamTagMember2IPersonaProps = (teamTagMember: TeamTagMember, coinSize?: number): ApprovalPersonaProps => {
    return { id: teamTagMember.UserId, text: teamTagMember.DisplayName, coinSize };
};

export const PeopleResults2IPersonaProps = (peopleResults: PeopleResult[]): IPersonaProps[] => {
    return peopleResults?.map(PeopleResult2IPersonaProps) ?? [];
};

export const MixedResults2IPersonaProps = (
    peopleResults?: PeopleResult[],
    tagDynamicItems?: DynamicRouteItemInput[]
): ApprovalPersonaProps[] => {
    const validPeopleResults = peopleResults?.filter((p: PeopleResult): boolean => !!p);
    const vadlidTagDynamicItems = tagDynamicItems?.filter((t: DynamicRouteItemInput): boolean => !!t);
    const peoplePersonas = validPeopleResults?.length ? validPeopleResults.map(PeopleResult2IPersonaProps) : [];
    const tagPersonas = vadlidTagDynamicItems?.length ? vadlidTagDynamicItems.map(TagDynamicItem2IPersonaProps) : [];
    return sortByText(peoplePersonas.concat(tagPersonas));
};

export const IPersonaProps2UserIds = (peopleResults: IPersonaProps[]): string[] => {
    return peopleResults?.map((peopleResult: IPersonaProps): string => peopleResult.id) ?? [];
};

export const IPersonaProps2PeopleResult = (peopleResult: ApprovalPersonaProps): PeopleResult => {
    return { UserAadObjectId: peopleResult.id, UserDisplayName: peopleResult.text, PersonType: null };
};

export const IPersonaProps2PeopleResults = (peopleResults: IPersonaProps[]): PeopleResult[] => {
    return peopleResults?.map(IPersonaProps2PeopleResult) ?? [];
};

/* Utilities for the type gap between Frontend and Backend*/
export type UI_StaticRouteInput = Omit<StaticRouteInput, 'Approvers'> & {
    Approvers: PeopleResult[] | undefined;
};

/* Utilities for the type gap between Frontend and Backend*/
export type UI_DynamicRouteItemInput = DynamicRouteItemInput & {
    TeamTagMemberCount?: number;
    TeamTagDisplayName?: string;
    TeamId?: string;
};
export type UI_DynamicRouteInput = Omit<DynamicRoute, 'Items'> & {
    Items: UI_DynamicRouteItemInput[] | undefined;
};
export const MixedRouteType = RouteTypes.StaticRoute | RouteTypes.DynamicRoute;

export type UI_FallbackPolicyInput = Omit<FallbackPolicyInput, 'FallbackApprovers'> & {
    FallbackApprovers: PeopleResult[] | undefined;
};

export function PredefinedApproversRouteResolveResults2PeopleResults(
    predefinedApprovers: PredefinedApproversRouteResolveResult[]
): PeopleResult[] {
    return (
        predefinedApprovers?.map((result: PredefinedApproversRouteResolveResult): PeopleResult => result.Approver) ?? []
    );
}

export function PredefinedApproversRouteResolveResults2IPersonaProps(
    predefinedApprovers: PredefinedApproversRouteResolveResult[]
): IPersonaProps[] {
    return (
        predefinedApprovers?.map(
            (result: PredefinedApproversRouteResolveResult): IPersonaProps =>
                PeopleResult2IPersonaProps(result.Approver)
        ) ?? []
    );
}

export function PredefinedApproversRouteResolveResults2UserIds(
    predefinedApprovers: PredefinedApproversRouteResolveResult[]
): string[] {
    return predefinedApprovers?.map((result: PredefinedApproversRouteResolveResult): string => result.ApproverId) ?? [];
}

export function PeopleResults2PredefinedApproversRouteResolveResults(
    peopleResults: PeopleResult[]
): PredefinedApproversRouteResolveResult[] {
    if (peopleResults?.length) {
        return peopleResults
            .filter((s: PeopleResult): boolean => !!s)
            .map(
                (peopleResult: PeopleResult): PredefinedApproversRouteResolveResult => ({
                    ResultCode: null,
                    ApproverId: peopleResult.UserAadObjectId,
                    Approver: peopleResult,
                })
            );
    } else {
        return [];
    }
}

// Template instance raw data to UI input data.
export function StaticRoute2UI_StaticRouteInput(staticRoute: StaticRoute): UI_StaticRouteInput {
    if (!staticRoute) return DefaultStaticRouteInput;

    const Approvers = PredefinedApproversRouteResolveResults2PeopleResults(staticRoute?.ResolveResult);
    const AllMustRespond = staticRoute?.AllMustRespond ?? true;
    return { ...staticRoute, Approvers, AllMustRespond };
}

// Template instance raw data to UI input data.
export function DynamicRoute2UI_DynamicRouteInput(dynamicRoute: DynamicRoute): UI_DynamicRouteInput {
    if (!dynamicRoute) return DefaultDynamicRouteInput;
    return {
        ...dynamicRoute,
        AllMustRespond: dynamicRoute?.AllMustRespond ?? true,
        Items: dynamicRoute.Items?.map((item: DynamicRouteItem): UI_DynamicRouteItemInput => {
            return {
                Type: item.Type,
                AdditionalProperties: item.AdditionalProperties,
                TeamId: item.TeamId,
                TeamTagMemberCount: item.TeamTagMemberCount,
                TeamTagId: item.TeamTagId,
                TeamTagDisplayName: item.TeamTagDisplayName,
            };
        }),
    };
}

// Template instance raw data to input data (for edit mode copy).
export function StaticRoute2StaticRouteInput(staticRoute: StaticRoute): StaticRouteInput {
    if (!staticRoute) return null;
    const Approvers = PredefinedApproversRouteResolveResults2UserIds(staticRoute?.ResolveResult);
    const AllMustRespond = staticRoute?.AllMustRespond ?? true;
    return { ...staticRoute, Approvers, AllMustRespond };
}

// UI input data to post backend data.
export function UI_StaticRouteInput2StaticRouteInput(ui_staticRouteInput: UI_StaticRouteInput): StaticRouteInput {
    const Approvers = PeopleResults2UserIds(ui_staticRouteInput.Approvers);
    const AllMustRespond = ui_staticRouteInput?.AllMustRespond ?? true;
    return { ...ui_staticRouteInput, Approvers, AllMustRespond };
}

// UI input data to post backend data.
export function UI_DynamicRouteInput2DynamicRouteInput(ui_dynamicRouteInput: UI_DynamicRouteInput): DynamicRouteInput {
    return { ...ui_dynamicRouteInput };
}

// UI input data to backend data (for preview).
export function UI_StaticRouteInput2StaticRoute(ui_staticRouteInput: UI_StaticRouteInput): StaticRoute {
    const ResolveResult = PeopleResults2PredefinedApproversRouteResolveResults(ui_staticRouteInput.Approvers);
    const AllMustRespond = ui_staticRouteInput?.AllMustRespond ?? true;
    return { ...ui_staticRouteInput, ResolveResult, AllMustRespond };
}

export function UI_DynamicRouteInput2DynamicRoute(dynamicRouteInput?: UI_DynamicRouteInput): DynamicRoute {
    if (!dynamicRouteInput) return undefined;
    const AllMustRespond = dynamicRouteInput.AllMustRespond ?? false;
    const Items = dynamicRouteInput.Items.map(
        (item: UI_DynamicRouteItemInput): DynamicRouteItem => ({
            ...item,
            ResultCode: null,
        })
    );
    return { AllMustRespond, Items };
}

// Template instance raw data to UI input data.
export function FallbackPolicy2UI_FallbackPolicyInput(fallbackPolicy: FallbackPolicy): UI_FallbackPolicyInput {
    if (!fallbackPolicy) return DefaultFallbackPolicyInput;
    const FallbackApprovers = PredefinedApproversRouteResolveResults2PeopleResults(fallbackPolicy?.FallbackApprovers);
    const FallbackApproversAllMustRespond = fallbackPolicy?.FallbackApproversAllMustRespond ?? true;
    return { ...fallbackPolicy, FallbackApprovers, FallbackApproversAllMustRespond };
}

// Template instance raw data to input data (for edit mode copy).
export function FallbackPolicy2FallbackPolicyInput(fallbackPolicy: FallbackPolicy): FallbackPolicyInput {
    if (!fallbackPolicy) return null;

    const FallbackApprovers = PredefinedApproversRouteResolveResults2UserIds(fallbackPolicy?.FallbackApprovers);
    const FallbackApproversAllMustRespond = fallbackPolicy?.FallbackApproversAllMustRespond ?? true;
    return { ...fallbackPolicy, FallbackApprovers, FallbackApproversAllMustRespond };
}

// UI input data to post backend data.
export function UI_FallbackPolicyInput2FallbackPolicyInput(
    ui_fallbackPolicy: UI_FallbackPolicyInput
): FallbackPolicyInput {
    if (ui_fallbackPolicy.Type === FallbackPolicyType.LetUserEnter) return { Type: FallbackPolicyType.LetUserEnter };
    else {
        const FallbackApprovers = PeopleResults2UserIds(ui_fallbackPolicy.FallbackApprovers);
        const FallbackApproversAllMustRespond = ui_fallbackPolicy?.FallbackApproversAllMustRespond ?? true;
        return { ...ui_fallbackPolicy, FallbackApprovers, FallbackApproversAllMustRespond };
    }
}

// UI input data to post backend data.
export function UI_StepRouteSettingInput2StepsRouteInput(items: UI_StepRouteSettingInput[]): StepRouteSettingInput[] {
    return items?.map((item: UI_StepRouteSettingInput): StepRouteSettingInput => {
        return {
            RouteTypes: item.approverRouteType,
            DynamicRoute: UI_DynamicRouteInput2DynamicRouteInput(item.dynamicRoute),
            StaticRoute: UI_StaticRouteInput2StaticRouteInput(item.staticRoute),
            FallbackPolicy: UI_FallbackPolicyInput2FallbackPolicyInput(item.fallbackPolicy),
        };
    });
}

// UI input data to backend data (for preview).
export function UI_FallbackPolicyInput2FallbackPolicy(ui_fallbackPolicy: UI_FallbackPolicyInput): FallbackPolicy {
    const FallbackApprovers = PeopleResults2PredefinedApproversRouteResolveResults(ui_fallbackPolicy.FallbackApprovers);
    const FallbackApproversAllMustRespond = ui_fallbackPolicy?.FallbackApproversAllMustRespond ?? true;
    return { ...ui_fallbackPolicy, FallbackApprovers, FallbackApproversAllMustRespond };
}

export function StepRouteSetting2UI_StepRouteSetting(stepRoutes: StepRouteSetting[]): UI_StepRouteSettingInput[] {
    return (
        stepRoutes?.map(
            (stepRoute: StepRouteSetting, index: number): UI_StepRouteSettingInput => ({
                order: index + 1,
                approverRouteType: stepRoute.RouteTypes,
                dynamicRoute: DynamicRoute2UI_DynamicRouteInput(stepRoute.DynamicRoute),
                fallbackPolicy: FallbackPolicy2UI_FallbackPolicyInput(stepRoute.FallbackPolicy),
                staticRoute: StaticRoute2UI_StaticRouteInput(stepRoute.StaticRoute),
            })
        ) || []
    );
}
export function WorkflowSchema2WorkflowSchemaInput(workflowSchema: WorkflowSchema): WorkflowSchemaInput {
    const workflowSchemaInput: any = { ...workflowSchema };
    (workflowSchemaInput as WorkflowSchemaInput).Approvers = PeopleResults2UserIds(workflowSchema.Approvers);
    (workflowSchemaInput as WorkflowSchemaInput).StaticRoute = StaticRoute2StaticRouteInput(workflowSchema.StaticRoute);
    (workflowSchemaInput as WorkflowSchemaInput).DynamicRoute = DynamicRoute2UI_DynamicRouteInput(
        workflowSchema.DynamicRoute
    );
    (workflowSchemaInput as WorkflowSchemaInput).FallbackPolicy = FallbackPolicy2FallbackPolicyInput(
        workflowSchema.FallbackPolicy
    );
    (workflowSchemaInput as WorkflowSchemaInput).StepRoutes = workflowSchema.StepRoutes?.map(
        (stepRoute: StepRouteSetting): StepRouteSettingInput => {
            return {
                RouteTypes: stepRoute.RouteTypes,
                DynamicRoute: DynamicRoute2UI_DynamicRouteInput(stepRoute.DynamicRoute),
                StaticRoute: StaticRoute2StaticRouteInput(stepRoute.StaticRoute),
                FallbackPolicy: FallbackPolicy2FallbackPolicyInput(stepRoute.FallbackPolicy),
            };
        }
    );
    return workflowSchemaInput;
}

export const TemplateInstanceValidator = {
    isApproversEmpty<T>(approvers: T[]): boolean {
        return !approvers || approvers.length === 0;
    },
    isApproversExceeding<T>(approvers: T[]): boolean {
        const maxNumberOfApprovers = getAppHost().getEcsConfig().MicrosoftTeamApprovals.MaxNumberOfApprovers;
        return approvers?.length > maxNumberOfApprovers;
    },
};

export const WorkflowSchemaConvertor = {
    V1ToV2: (workflowSchema: WorkflowSchema): WorkflowSchema => {
        const v2WorkflowSchema: WorkflowSchema = { ...workflowSchema };
        v2WorkflowSchema.Version = 'v2.0';
        if (!workflowSchema.Approvers || workflowSchema.Approvers.length === 0) {
            v2WorkflowSchema.RouteTypes = RouteTypes.LetUserEnter;
            v2WorkflowSchema.AllMustRespond = workflowSchema.AllMustRespond;
        } else {
            v2WorkflowSchema.RouteTypes = RouteTypes.StaticRoute;
            v2WorkflowSchema.StaticRoute = {
                AllMustRespond: workflowSchema.AllMustRespond,
                ResolveResult: PeopleResults2PredefinedApproversRouteResolveResults(workflowSchema.Approvers),
            };
        }
        return v2WorkflowSchema;
    },
    V2ToV1: (workflowSchema: WorkflowSchema): WorkflowSchema => {
        const v1WorkflowSchema: WorkflowSchema = { ...workflowSchema };
        v1WorkflowSchema.Version = 'v1.0';
        if (
            workflowSchema.RouteTypes === RouteTypes.LetUserEnter ||
            workflowSchema.RouteTypes === RouteTypes.DynamicRoute
        ) {
            v1WorkflowSchema.Approvers = [];
            v1WorkflowSchema.AllMustRespond = true;
        } else {
            v1WorkflowSchema.Approvers = PredefinedApproversRouteResolveResults2PeopleResults(
                workflowSchema.StaticRoute.ResolveResult
            );
            v1WorkflowSchema.AllMustRespond = workflowSchema.StaticRoute.AllMustRespond;
        }

        return v1WorkflowSchema;
    },
};

/**
 * @see https://usehooks-typescript.com/react-hook/use-is-mounted
 */
export function useIsMounted(): () => boolean {
    const isMounted = useRef(false);

    useEffect((): (() => void) => {
        isMounted.current = true;

        return (): void => {
            isMounted.current = false;
        };
    }, []);

    return (): boolean => isMounted.current;
}
